import IcMenu from '@assets/details-camera-icons/IcMenu';
import IcSetting from '@assets/details-camera-icons/IcSetting';
import BaseCheckbox from '@components/BaseCheckbox';
import BreadCrumbs, { IBreadCrumbs } from '@components/breadcumbs/BreadCumbs';
import IOSSwitch from '@components/IOSSwitch';
import StyledAutocomplete from '@components/styled-autocomplete';
import { ContraventionReasonTranslations, GeoZoneCoordinates, GeoZones, GeoZoneType, Location } from '@EcamModel/model';
import { Zones } from '@EcamModel/model/Zones';
import { LoadingButton } from '@mui/lab';
import { Box, Button, FormControlLabel, Stack, Tooltip, Typography } from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers';
import { validateDatePickerValue } from '@pages/cameras/add-new';
import moment from 'moment';
import { Controller, FormProvider } from 'react-hook-form';
import { BsQuestion } from 'react-icons/bs';
import { useNavigate, useParams } from 'react-router-dom';
import { contraventionReasonEV, contraventionReasonNoRoadway, detectModes, GeoZoneGracePeriod, HeadingSection } from '../../../AddGeoZones';
import { Mode } from '../../../CameraInformation';
import GracePeriods from '../../../GracePeriods';
import OperationalHours from '../../../OperationalHours';
import FramePolygonGeoOverview from '../../components/FramePolygonGeoOverview';
import useAddGeoZones from '../../ptz/upsertGeoZones/hook/useAddGeoZones';
import LoadingBreadCrumbs from '@components/skeleton/LoadingBreadCrumbs';
import { updateGracePeriods } from '@pages/cameras/details/utils/gracePeriodUtils';
import { renderDetectModesOption } from '../../ptz/upsertGeoZones/components/EditGeoZonesPTZ';

export default function AddGeoZonesSP111() {
    const {
        setStartDate,
        updateOperationHours,
        setIsOverlap,
        handleAddGeoZoneOverview,
        shouldDisableButton,
        handleNavigateButton,
        prevGeoZonePointsDetailPageCameraSP111,
        conditionBreadcrumbs,
        loading,
        errors,
        methods,
        contraventionAutocomplete,
        detectModeAutocomplete,
        gracePeriodForm,
        operationHours,
        isLoadingSave,
        startDate,
        imageElementSP111,
        locationAutocomplete,
        zoneAutocomplete,
        zones,
        setZones,
    } = useAddGeoZones({ isCheckGeoZoneDetail: true });

    const setValue = methods.setValue as (name: string, value: any, options?: Record<string, boolean>) => void;

    const handleDeleteGeoZonePoints = () => {
        setValue('geoZonePoints', [] as GeoZoneCoordinates[], {
            shouldValidate: true,
            shouldTouch: true,
            shouldDirty: true,
        });
    };

    return (
        <Stack mt={1} width={'100%'}>
            <Typography variant="h3" mb={1}>
                Add geo-zones
            </Typography>
            {loading ? <LoadingBreadCrumbs /> : <BreadCrumbs breadcrumbs={conditionBreadcrumbs} />}

            <FormProvider {...methods}>
                <Box mt={3}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                        <Controller
                            name="contraventionReasonTranslations"
                            control={methods.control}
                            rules={{
                                required: {
                                    value: true,
                                    message: 'Please select a Contravention reason.',
                                },
                            }}
                            render={({ field, fieldState: { error } }) => (
                                <StyledAutocomplete
                                    {...field}
                                    {...contraventionAutocomplete}
                                    isRequired
                                    getOptionLabel={(o) => o.Summary}
                                    label="Contravention reason"
                                    value={field.value}
                                    placeholder="Select reason"
                                    disabledAllOption
                                    onChange={(c: ContraventionReasonTranslations) => {
                                        const currentGracePeriods = methods.watch()
                                            .gracePeriods as Required<GeoZoneGracePeriod>[];
                                        const updatedGracePeriods = updateGracePeriods({
                                            currentGracePeriods,
                                            isZeroGracePeriod: [
                                                contraventionReasonEV,
                                                contraventionReasonNoRoadway,
                                            ].includes(c.ContraventionReasonId),
                                        });

                                        setValue('gracePeriods', updatedGracePeriods);
                                        setValue('contraventionReasonTranslations', c!, {
                                            shouldValidate: true,
                                            shouldTouch: true,
                                            shouldDirty: true,
                                        });
                                        methods.clearErrors();
                                    }}
                                    wrapperWidth="100%"
                                />
                            )}
                        />
                        <Controller
                            name="detectMode"
                            control={methods.control}
                            rules={{
                                required: {
                                    value: true,
                                    message: 'Please select a detectode.',
                                },
                            }}
                            render={({ field, fieldState: { error } }) => (
                                <StyledAutocomplete
                                    {...field}
                                    {...detectModeAutocomplete}
                                    isRequired
                                    getOptionLabel={(o) => o.Label}
                                    customRenderOption={(_, option) => renderDetectModesOption(option, detectModes)}
                                    label="Detect mode"
                                    value={field.value}
                                    disabledAllOption
                                    onChange={(c) => {
                                        field.onChange(c);
                                    }}
                                    wrapperWidth="100%"
                                />
                            )}
                        />
                        <div>
                            <Controller
                                name="isActive"
                                control={methods.control}
                                render={({ field, fieldState: { error } }) => (
                                    <IOSSwitch
                                        {...field}
                                        label={
                                            <Typography sx={{ minWidth: '55px' }}>
                                                {!!field.value ? 'Active' : 'Inactive'}
                                            </Typography>
                                        }
                                        iosSwitchProps={{
                                            checked: field.value,
                                            onChange(event, checked) {
                                                field.onChange(event);
                                            },
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </Stack>

                    {/* checkbox ExemptVRNs */}
                    <Stack direction="row" alignItems="center" justifyContent={'space-between'} width="100%" mt={2}>
                        <Stack direction="row" alignItems="center">
                            <Controller
                                name="isExemptVRNs"
                                control={methods.control}
                                render={({ field, fieldState: { error } }) => (
                                    <FormControlLabel
                                        control={
                                            <BaseCheckbox
                                                checked={field.value}
                                                onChange={(_, checked) => {
                                                    field.onChange(checked);
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                }}
                                            />
                                        }
                                        label={
                                            <Typography ml={0.5} mt={0.3}>
                                                Exempt VRNs on Permit lists
                                            </Typography>
                                        }
                                        sx={{ ml: 0 }}
                                    />
                                )}
                            />
                        </Stack>
                    </Stack>
                    {/* end checkbox ExemptVRNs  */}

                    {/* Location and Zone */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} mt={2}>
                        <Controller
                            name="location"
                            control={methods.control}
                            render={({ field, fieldState: { error } }) => (
                                <StyledAutocomplete
                                    {...field}
                                    {...locationAutocomplete}
                                    getOptionLabel={(o) => (o as Location)?.Name}
                                    label="Location"
                                    value={field.value}
                                    placeholder="Select location"
                                    disabledAllOption
                                    onChange={(l: Location) => {
                                        setValue('location', l!, {
                                            shouldValidate: true,
                                            shouldTouch: true,
                                            shouldDirty: true,
                                        });

                                        // Set zones from the selected location (already merged if applicable)
                                        setZones(l?.Zones || []);

                                        // Auto-select camera's zone if it's in the list, otherwise select first zone
                                        if (l?.Zones && l.Zones.length > 0) {
                                            const cameraZone = l.Zones.find((z) => z.Id === methods.getValues('zone')?.Id);
                                            setValue('zone', cameraZone || l.Zones[0]);
                                        } else {
                                            setValue('zone', undefined as any);
                                        }
                                    }}
                                    wrapperWidth="100%"
                                />
                            )}
                        />
                        <Controller
                            name="zone"
                            control={methods.control}
                            render={({ field, fieldState: { error } }) => (
                                <StyledAutocomplete
                                    {...field}
                                    {...zoneAutocomplete}
                                    getOptionLabel={(o) => `${(o as Zones)?.PublicName} (ZoneID ${(o as Zones)?.Id})`}
                                    label="Zone"
                                    value={field.value}
                                    placeholder="Select zone"
                                    disabledAllOption
                                    disabled={!methods.watch().location}
                                    onChange={(z: Zones) => {
                                        setValue('zone', z!, {
                                            shouldValidate: true,
                                            shouldTouch: true,
                                            shouldDirty: true,
                                        });
                                    }}
                                    wrapperWidth="100%"
                                />
                            )}
                        />
                    </Stack>
                    {/* end Location and Zone */}

                    <>
                        {/* Draw angle box */}
                        <HeadingSection icon={<IcMenu />} heading="Zone marking" />
                        <Controller
                            name="geoZonePoints"
                            control={methods.control}
                            render={({ field, fieldState: { error } }) => (
                                <FramePolygonGeoOverview
                                    {...field}
                                    imageElement={imageElementSP111!}
                                    mode={Mode.create}
                                    prevGeoZone={prevGeoZonePointsDetailPageCameraSP111 as GeoZoneCoordinates[][]}
                                    selectedGeoZone={{} as GeoZones}
                                    geoZoneType={GeoZoneType.Detail}
                                    onChange={(points) => {
                                        setValue('geoZonePoints', points, {
                                            shouldValidate: true,
                                            shouldTouch: true,
                                            shouldDirty: true,
                                        });
                                    }}
                                    onDelete={handleDeleteGeoZonePoints}
                                />
                            )}
                        />
                    </>

                    {/* Operational periods */}
                    <HeadingSection icon={<IcSetting />} heading="Time setting" />
                    <Stack
                        p={1.7}
                        sx={{
                            border: '1px solid #DDDDDD',
                            borderWidth: '0px 1px 1px 1px',
                            borderRadius: '0px 0px 4px 4px',
                        }}
                        spacing={2}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            sx={{ borderBottom: '1px solid #DDDDDD', pb: 1.7 }}
                        >
                            <Typography>
                                Effective from<span style={{ color: 'red' }}>*</span>:
                            </Typography>
                            <DesktopDatePicker
                                value={startDate}
                                format="DD/MM/YYYY"
                                shouldDisableDate={(date) => validateDatePickerValue(moment(date).toDate()) !== null}
                                onChange={(e) => setStartDate(e!)}
                                slotProps={{
                                    textField: {
                                        inputProps: {
                                            readOnly: true,
                                        },
                                        sx: {
                                            width: 250,
                                        },
                                    },
                                }}
                            />
                        </Stack>
                        <Stack sx={{ borderBottom: '1px solid #DDDDDD', pb: 2 }} spacing={2}>
                            <Typography>
                                Grace period setting<span style={{ color: 'red' }}>*</span>:
                            </Typography>
                            <GracePeriods
                                gracePeriods={gracePeriodForm}
                                control={methods.control}
                                errors={errors}
                                watch={methods.watch}
                            />
                        </Stack>

                        {operationHours.map((item, index, arr) => {
                            return (
                                <OperationalHours
                                    geoZoneHourConfigWithTimeRanges={item as any}
                                    geoZoneGracePeriod={methods.watch().gracePeriods}
                                    onChange={(item, error) => {
                                        const operationHours = {
                                            Weekday: item.Weekday,
                                            TimeRanges: item.TimeRanges,
                                            AllDay: item.AllDay,
                                            Status: item.Status,
                                        };
                                        updateOperationHours(index, operationHours);

                                        setIsOverlap(error);
                                    }}
                                />
                            );
                        })}
                    </Stack>
                </Box>
            </FormProvider>

            {/* Button */}
            <Stack mt={3} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                <Button
                    variant="cancel"
                    sx={{ flexBasis: 130 }}
                    disabled={isLoadingSave}
                    onClick={handleNavigateButton}
                >
                    Cancel
                </Button>
                <LoadingButton
                    sx={{
                        minWidth: 130,
                    }}
                    loading={isLoadingSave}
                    loadingPosition="center"
                    variant="contained"
                    disabled={shouldDisableButton() || isLoadingSave}
                    onClick={handleAddGeoZoneOverview()}
                >
                    Save
                </LoadingButton>
            </Stack>
        </Stack>
    );
}
